import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOrg } from "../../contexts/OrgContext";
import {
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlinePhone,
  HiOutlineDocumentReport,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineClipboardCheck,
  HiOutlineLogout,
  HiOutlineClipboardList,
  HiOutlineCog,
  HiOutlineSparkles,
  HiOutlineMicrophone,
  HiOutlineCube,
  HiOutlineDocumentText,
} from "react-icons/hi";
import logo from "../../assets/logo.png";

const navItems = [
  { path: "/app/", label: "Dashboard", icon: HiOutlineHome, exact: true },
  { path: "/app/leads", label: "Leads", icon: HiOutlineUsers, exact: false },
  { path: "/app/users", label: "Users", icon: HiOutlineUsers, exact: false },
  {
    path: "/app/call-logs",
    label: "Call Logs",
    icon: HiOutlinePhone,
    exact: false,
  },
  {
    path: "/app/recordings",
    label: "Recordings",
    icon: HiOutlineMicrophone,
    exact: false,
  },
  {
    path: "/app/attendance",
    label: "Attendance",
    icon: HiOutlineClipboardCheck,
    exact: false,
  },
  {
    path: "/app/leaderboard",
    label: "Leaderboard",
    icon: HiOutlineSparkles,
    exact: true,
  },
  {
    path: "/app/reports",
    label: "Reports",
    icon: HiOutlineDocumentReport,
    exact: true,
  },
  {
    path: "/app/reports/daily-work",
    label: "Daily Work",
    icon: HiOutlineClipboardList,
    exact: true,
  },
  {
    path: "/app/products",
    label: "Products",
    icon: HiOutlineCube,
    exact: false,
  },
  {
    path: "/app/scripts",
    label: "Scripts",
    icon: HiOutlineDocumentText,
    exact: false,
  },
  {
    path: "/app/settings",
    label: "Settings",
    icon: HiOutlineCog,
    exact: false,
    feature: "googleSheetIntegration", // Only show if this feature is enabled
  },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { canUseFeature, plan, loading } = useOrg();
  const location = useLocation();

  const isActive = (path, exact = false) => {
    if (path === "/") return location.pathname === "/";
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onClose();
  };

  // Filter nav items based on plan features
  const filteredNavItems = navItems.filter((item) => {
    // If item has a feature requirement, check if user has access
    // if (item.feature) {
    //   const hasFeature = canUseFeature(item.feature);
    //   console.log(`[Sidebar] Feature check for ${item.feature}:`, hasFeature, 'Plan:', plan?.name);
    //   return hasFeature;
    // }
    return true;
  });

  // Debug: Log plan info
  React.useEffect(() => {
    if (!loading && plan) {
      console.log('[Sidebar] Plan loaded:', plan.name, 'Features:', plan.features);
    }
  }, [loading, plan]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Professional Design */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-gradient-to-b from-white to-secondary-50/30 border-r border-secondary-200/50
          flex flex-col shadow-xl lg:shadow-none
          transform transition-transform duration-300 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        {/* Logo - full width, no text */}
        <div className="relative flex items-center justify-center px-5 py-4 border-b border-secondary-100/50">
          {/* logo.png is a wide wordmark banner — let it span the sidebar width
              at its natural aspect ratio (capped height so it stays tidy). */}
          <img
            src={logo}
            alt="TeleValley"
            className="w-full h-auto max-h-16 object-contain"
          />
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 rounded-xl hover:bg-secondary-100 lg:hidden transition-colors"
            aria-label="Close sidebar"
          >
            <HiOutlineX className="w-5 h-5 text-secondary-400" />
          </button>
        </div>

        {/* User Info - Professional */}
        <div className="px-4 py-3">
          <div className="px-3 py-3 rounded-xl bg-gradient-to-r from-primary-50 to-indigo-50 border border-primary-100/50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md ring-2 ring-white">
                <span className="text-white font-semibold text-sm">
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-secondary-900 text-sm truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-secondary-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation - Professional */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          <p className="px-3 text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2">
            Menu
          </p>
          <ul className="space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 relative
                      ${
                        active
                          ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25"
                          : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                      }
                    `}
                  >
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/30 rounded-r-full" />
                    )}
                    <Icon
                      className={`w-5 h-5 flex-shrink-0 ${active ? "text-white" : "text-secondary-400"}`}
                    />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout - Professional */}
        <div className="p-3 border-t border-secondary-100/50">
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-secondary-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:text-red-600 transition-all duration-200"
          >
            <HiOutlineLogout className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const MobileHeader = ({ onMenuClick }) => {
  const { user } = useAuth();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-secondary-200/50 shadow-sm">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-xl text-secondary-600 hover:bg-secondary-100 transition-colors"
        >
          <HiOutlineMenu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl overflow-hidden shadow-md flex items-center justify-center">
            <img
              src={logo}
              alt="TeleValley"
              className="w-full h-full object-contain"
            />
          </div>
          <span className="font-bold text-secondary-900">TeleValley</span>
        </div>
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center ring-2 ring-white shadow-sm">
          <span className="text-primary-600 font-semibold text-xs">
            {user?.firstName?.charAt(0)}
            {user?.lastName?.charAt(0)}
          </span>
        </div>
      </div>
    </header>
  );
};

export default Sidebar;
